if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.boundKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        // ModalDialog.connectedCallback() relocates this element to <body>; skipping it
        // leaves the lightbox buried in the section and unable to open.
        super.connectedCallback();
        if (this.controlsBound) return;
        this.controlsBound = true;

        [
          [this.querySelector('[data-media-prev]'), -1],
          [this.querySelector('[data-media-next]'), 1],
        ].forEach(([button, direction]) => {
          if (!button) return;
          button.addEventListener('click', () => this.step(direction));
          // .media-modal closes on any mouse pointerup that bubbles up to the modal,
          // so the arrows have to stop their own events from reaching it.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.querySelector('.product-media-modal__counter')?.addEventListener('pointerup', (event) =>
          event.stopPropagation()
        );

        // Same reason: a click on the photo itself used to dismiss the lightbox, which
        // is easy to do by accident while examining a stone or a hallmark. Closing
        // stays on the X, Esc, and the surrounding backdrop.
        this.querySelectorAll('[role="document"] > [data-media-id]').forEach((media) => {
          media.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.addSwipeListeners();
      }

      hide() {
        document.removeEventListener('keydown', this.boundKeydown);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        document.addEventListener('keydown', this.boundKeydown);
      }

      // Media the shopper can actually page through. When hide_variants is on, the
      // duplicate variant images are hidden by CSS — keep them out of the count so
      // the "2 / 5" counter matches what's on screen.
      get mediaItems() {
        const items = Array.from(this.querySelectorAll('[role="document"] > [data-media-id]'));
        const variants = items.filter((item) => item.classList.contains('product__media-item--variant'));
        return items.filter(
          (item) => !item.classList.contains('product__media-item--variant') || item === variants[0]
        );
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      }

      addSwipeListeners() {
        const surface = this.querySelector('[role="document"]');
        if (!surface) return;
        let startX = null;
        let startY = null;

        surface.addEventListener(
          'touchstart',
          (event) => {
            startX = event.changedTouches[0].clientX;
            startY = event.changedTouches[0].clientY;
          },
          { passive: true }
        );

        surface.addEventListener(
          'touchend',
          (event) => {
            if (startX === null) return;
            const deltaX = event.changedTouches[0].clientX - startX;
            const deltaY = event.changedTouches[0].clientY - startY;
            // Ignore vertical drags — those are the shopper panning a zoomed-in photo.
            if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
              this.step(deltaX < 0 ? 1 : -1);
            }
            startX = null;
            startY = null;
          },
          { passive: true }
        );
      }

      step(direction) {
        const items = this.mediaItems;
        if (items.length < 2) return;
        const nextIndex = (this.currentIndex + direction + items.length) % items.length;
        this.setActiveIndex(nextIndex);
      }

      setActiveIndex(index) {
        const items = this.mediaItems;
        const activeMedia = items[index];
        if (!activeMedia) return;

        this.currentIndex = index;
        items.forEach((item) => item.classList.remove('active'));
        activeMedia.classList.add('active');
        this.loadDeferredContent(activeMedia);
        this.updateCounter(index, items.length);

        const container = this.querySelector('[role="document"]');
        container.scrollTop = 0;
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
      }

      updateCounter(index, total) {
        const current = this.querySelector('[data-media-current]');
        const totalEl = this.querySelector('[data-media-total]');
        if (current) current.textContent = index + 1;
        if (totalEl) totalEl.textContent = total;
      }

      loadDeferredContent(activeMedia) {
        const template = activeMedia.querySelector('template');
        const content = template ? template.content : null;
        if (activeMedia.nodeName == 'DEFERRED-MEDIA' && content && content.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }

      showActiveMedia() {
        const openedId = this.openedBy.getAttribute('data-media-id');
        const items = this.mediaItems;
        const index = items.findIndex((item) => item.dataset.mediaId === openedId);
        this.setActiveIndex(index === -1 ? 0 : index);
      }
    }
  );
}
